import * as React from 'react';

import { cn } from 'lib/utils';

/**
 * Slider horizontal de cards para móvil/tablet, con desplazamiento NATIVO del navegador
 * (`scroll-snap`). Sin librería de carrusel: Embla medía el ancho de las cards justo cuando el
 * documento tenía una barra de desplazamiento clásica (15 px menos) y no recalculaba cuando
 * desaparecía, así que todas las paradas quedaban desplazadas y el error se acumulaba (la última
 * card se quedaba ~50 px a la derecha, cortada). Con scroll nativo el navegador usa siempre el
 * layout real.
 *
 * Reglas que no se dejan al consumidor, porque son lo que hace que se lea como slider:
 *
 * - **Todas las cards miden lo mismo** (`slideBasis`, por defecto el 88 % del ancho) y se paran
 *   alineadas a la izquierda: **siempre asoma un trozo de la siguiente**, nunca de la anterior.
 *   La última se queda alineada a la derecha, y a su izquierda asoma un poquito de la anterior
 *   para que no parezca una card aislada (es el final del scroll: no hay siguiente que asomar).
 * - **Puntitos reforzados**: el inactivo es `muted-foreground` y el activo `primary-text`, los dos
 *   por encima de 3:1 contra el fondo de página (WCAG 1.4.11). Con `muted-foreground` al 45 % y
 *   `primary` se quedaban en 1,7:1 y 3,0:1. El activo es además una píldora más larga. Cada puntito navega y su botón mide 44×44 sin solaparse.
 * - **Arrastre con ratón** en escritorio (con el dedo ya lo hace el navegador), sin seleccionar
 *   texto ni disparar el clic de lo que haya bajo el cursor (lo evita `setPointerCapture`).
 *
 * Todas las cards miden lo que la más alta: quien use esto debería condensar el contenido
 * opcional de sus cards para que no haya huecos (ver `condensed` en `ui/PlanCard`). Pensado para
 * `lg:hidden`: en escritorio, las cards van apiladas o en rejilla.
 */
export interface CardSliderItem {
	id: string;
	/** Para los puntitos: «Ir a <label>» (lectores de pantalla). */
	label: string;
	content: React.ReactNode;
}

export interface CardSliderProps {
	items: CardSliderItem[];
	/** Nombre accesible del slider. */
	ariaLabel: string;
	/** Clase de Tailwind `basis-*` de cada slide: cuánto de la siguiente se asoma. */
	slideBasis?: string;
	className?: string;
}

/** Píxeles que hay que arrastrar con el ratón para pasar a la card siguiente o anterior. */
const UMBRAL_ARRASTRE = 40;

export const CardSlider = ({
	items,
	ariaLabel,
	slideBasis = 'basis-[88%]',
	className
}: CardSliderProps) => {
	const trackRef = React.useRef<HTMLDivElement>(null);
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	const drag = React.useRef<{ x: number; scrollLeft: number; moved: boolean } | null>(null);
	const [dragging, setDragging] = React.useState(false);

	/** Distancia entre el inicio de una card y el de la siguiente (ancho + hueco), del layout real. */
	const step = React.useCallback(() => {
		const slides = trackRef.current?.querySelectorAll<HTMLElement>('[data-slide]');
		if (!slides || slides.length < 2) return 0;
		return slides[1].offsetLeft - slides[0].offsetLeft;
	}, []);

	const scrollToIndex = React.useCallback(
		(index: number) => {
			trackRef.current?.scrollTo({ left: index * step(), behavior: 'smooth' });
		},
		[step]
	);

	const onScroll = () => {
		const track = trackRef.current;
		const paso = step();
		if (!track || !paso) return;
		// Al final del scroll, la última card queda alineada a la derecha: con cards estrechas
		// (`slideBasis` pequeño) el redondeo no llegaba nunca a ella y su puntito no se activaba.
		const alFinal = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
		const index = alFinal ? items.length - 1 : Math.round(track.scrollLeft / paso);
		setSelectedIndex(Math.min(items.length - 1, Math.max(0, index)));
	};

	const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		// Solo el ratón: con el dedo el navegador ya desliza y encaja solo.
		if (event.pointerType !== 'mouse' || event.button !== 0 || !trackRef.current) return;
		drag.current = { x: event.clientX, scrollLeft: trackRef.current.scrollLeft, moved: false };
	};

	const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		const current = drag.current;
		const track = trackRef.current;
		if (!current || !track) return;
		const delta = event.clientX - current.x;
		if (!current.moved && Math.abs(delta) < 5) return;
		if (!current.moved) {
			current.moved = true;
			setDragging(true);
			// Capturar el puntero: si se suelta fuera del slider (o del marco de Storybook), el
			// arrastre termina igual en vez de quedarse a medias.
			track.setPointerCapture(event.pointerId);
		}
		track.scrollLeft = current.scrollLeft - delta;
	};

	const onPointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
		const current = drag.current;
		const track = trackRef.current;
		drag.current = null;
		if (!current?.moved || !track) return;
		if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
		const delta = event.clientX - current.x;
		const paso = step() || 1;
		// Si se arrastró lo bastante, pasa a la card siguiente o anterior a la de partida; si no,
		// vuelve a ella.
		const inicial = Math.round(current.scrollLeft / paso);
		const destino = Math.abs(delta) >= UMBRAL_ARRASTRE ? inicial + (delta < 0 ? 1 : -1) : inicial;
		setDragging(false);
		scrollToIndex(Math.min(items.length - 1, Math.max(0, destino)));
	};

	return (
		<div className={className}>
			<div
				ref={trackRef}
				role="region"
				// Enfocable para poder desplazarlo con las flechas: si ninguna card trae nada enfocable
				// (una cifra sin tooltip), el scroll solo funcionaría con ratón o dedo.
				tabIndex={0}
				aria-roledescription="carousel"
				aria-label={ariaLabel}
				onScroll={onScroll}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerEnd}
				onPointerCancel={onPointerEnd}
				className={cn(
					// `relative`: los hijos `absolute` (los `sr-only` de las cards) se recortan con el scroll del
					// track; sin esto se escapan y ensanchan el documento (barras de desplazamiento fuera).
					'relative flex gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					'rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
					// Durante el arrastre con ratón se apaga el encaje para que el scroll siga al cursor, y no
					// se selecciona texto (fuera del arrastre sí: una cifra se puede copiar). Sin `touch-pan-x`:
					// el dedo ya desliza en horizontal con el scroll nativo, y con él no se podía bajar por la
					// página empezando el gesto sobre una card.
					dragging ? 'cursor-grabbing snap-none select-none' : 'snap-x snap-mandatory'
				)}
			>
				{items.map((item, index) => (
					<div
						key={item.id}
						data-slide=""
						role="group"
						aria-roledescription="slide"
						aria-label={`${index + 1} de ${items.length}`}
						// `stop: always`: un gesto fuerte no se salta cards. Sin `items-start`: estiradas, todas
						// miden lo que la más alta.
						className={cn(
							'min-w-0 shrink-0 grow-0 snap-start snap-always [&>*]:h-full',
							slideBasis
						)}
					>
						{item.content}
					</div>
				))}
			</div>

			{items.length > 1 && (
				// Botones normales con `aria-current` en el activo (el «picker» del patrón de carrusel de
				// APG), no pestañas: unas pestañas exigen flechas, un solo tabindex y paneles enlazados.
				<div className="mt-1 flex items-center justify-center">
					{items.map((item, index) => (
						// El botón mide 44×44 de verdad (regla de área táctil) y el puntito va dentro: con un
						// pseudoelemento de 44 px sobre un punto de 10, los de al lado se pisaban y pulsar el
						// puntito 1 seleccionaba el 2.
						<button
							key={item.id}
							type="button"
							aria-current={index === selectedIndex ? 'true' : undefined}
							aria-label={`Ir a ${item.label} (${index + 1} de ${items.length})`}
							onClick={() => scrollToIndex(index)}
							className="flex size-11 cursor-pointer items-center justify-center"
						>
							<span
								aria-hidden="true"
								className={cn(
									'h-2.5 rounded-full transition-all',
									index === selectedIndex ? 'w-7 bg-primary-text' : 'w-2.5 bg-muted-foreground'
								)}
							/>
						</button>
					))}
				</div>
			)}
		</div>
	);
};
